import type { CampaignSchema } from "@/types";

/**
 * The coin's ring shows what is running out, never the player's progress
 * (IGB-RING-01). It drains clockwise from 12 o'clock over the remaining
 * fraction. Once a run is closing soon the ring turns urgent and drains the
 * closing window itself (a full orange ring over the final hour), because the
 * last hour of a week-long run is a sub-pixel sliver of the whole. A type with
 * nothing running out has no ring at all. Each type module says what its ring
 * drains through `launcher.ring`; the helpers here are the shared recipes.
 */
export interface RingState {
  /** 0..1 of the resource still left — of the closing window while urgent. */
  fraction: number;
  urgent: boolean;
}

/** The shared "closing soon" window for a run that ends at a fixed time. */
export const CLOSING_SOON_MS = 60 * 60 * 1000;

/**
 * Urgent = closing soon in absolute time, the one predicate the type's own
 * views use — never a fraction of the duration, which would paint a 30-day
 * tournament urgent for days.
 */
export function closingSoon(msLeft: number, windowMs: number): boolean {
  return msLeft <= windowMs;
}

/** The ring of a campaign that runs out of time between its start and end. */
export function timeLeftRing(
  campaign: Pick<CampaignSchema, "start" | "end">,
  now: number,
  windowMs = CLOSING_SOON_MS
): RingState | null {
  const start = Date.parse(campaign.start);
  const end = Date.parse(campaign.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start)
    return null;
  const msLeft = end - now;
  // Nothing left to drain: the ring goes, it never sits at empty.
  if (msLeft <= 0) return null;
  const urgent = closingSoon(msLeft, windowMs);
  const span = urgent ? Math.min(windowMs, end - start) : end - start;
  return { fraction: Math.min(1, msLeft / span), urgent };
}
